/**
 * Sequential Year-Based ID Generator
 * Generates IDs in format: PREFIX###-YY (e.g., D001-25, I003-25, M012-25)
 * 
 * @param {Array} existingRecords - Array of existing records with 'id' field
 * @param {string} prefix - Single letter prefix for the record type
 * @returns {string} - Generated ID in format PREFIX###-YY
 */
export const generateSequentialId = (existingRecords = [], prefix) => {
  // FT-209 FIX: Guard against non-array input (e.g., when called with just prefix)
  const records = Array.isArray(existingRecords) ? existingRecords : [];

  const currentYear = new Date().getFullYear();
  const yearSuffix = currentYear.toString().slice(-2); // Get last 2 digits of year

  // Filter existing records by prefix and current year
  const currentYearRecords = records.filter(record => {
    if (!record?.id) return false;
    const idParts = record.id.match(new RegExp(`^${prefix}(\\d{3})-(\\d{2})$`));
    return idParts && idParts[2] === yearSuffix;
  });
  
  // Find the highest number for this year
  let nextNumber = 1;
  if (currentYearRecords.length > 0) {
    const numbers = currentYearRecords.map(record => {
      const match = record.id.match(new RegExp(`^${prefix}(\\d{3})-(\\d{2})$`));
      return match ? parseInt(match[1], 10) : 0;
    });
    nextNumber = Math.max(...numbers) + 1;
  }
  
  // Format with leading zeros (supports up to 999 records per year)
  const formattedNumber = String(nextNumber).padStart(3, '0');
  return `${prefix}${formattedNumber}-${yearSuffix}`;
};

/**
 * Common prefixes for different record types
 * Can be extended as needed
 */
export const ID_PREFIXES = {
  DEFECT: 'D',
  REPAIR: 'R',
  INCIDENT: 'I',
  DRILL: 'DR',
  MAINTENANCE: 'M',
  TRAINING: 'T',
  AUDIT: 'A',
  INSPECTION: 'IN',
  CERTIFICATE: 'C',
  DOCUMENT: 'DOC',
  RISK_ASSESSMENT: 'RA',
  PERMIT: 'P',
  NONCONFORMITY: 'NC',
  OBSERVATION: 'O',
  NEAR_MISS: 'NM',
  ACCIDENT: 'AC',
  VOYAGE: 'V',
  EQUIPMENT: 'E',
  HOURS: 'H'
};

/**
 * Helper function to get next ID for specific record type
 * @param {Array} records - Existing records
 * @param {string} type - Record type (key from ID_PREFIXES)
 * @returns {string} - Generated ID
 */
export const getNextId = (records, type) => {
  const prefix = ID_PREFIXES[type];
  if (!prefix) {
    throw new Error(`Unknown record type: ${type}`);
  }
  return generateSequentialId(records, prefix);
};

/**
 * Parse an ID to extract its components
 * @param {string} id - ID to parse
 * @returns {object} - { prefix, number, year }
 */
export const parseSequentialId = (id) => {
  const match = id.match(/^([A-Z]+)(\d{3})-(\d{2})$/);
  if (!match) {
    return null;
  }
  
  return {
    prefix: match[1],
    number: parseInt(match[2], 10),
    year: `20${match[3]}`
  };
};

/**
 * Format a date-based reference number (alternative format)
 * @param {string} prefix - Prefix for the reference
 * @returns {string} - Reference like "INC-2025-08-001"
 */
export const generateDateBasedReference = (existingRecords = [], prefix) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  
  // Filter records from current month
  const currentMonthRecords = existingRecords.filter(record => {
    if (!record?.id) return false;
    return record.id.startsWith(`${prefix}-${year}-${month}-`);
  });
  
  const nextNumber = currentMonthRecords.length + 1;
  const formattedNumber = String(nextNumber).padStart(3, '0');
  
  return `${prefix}-${year}-${month}-${formattedNumber}`;
};